import React from 'react';

import Button, { ButtonProps } from '@material-ui/core/Button';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import Typography, { TypographyProps } from '@material-ui/core/Typography';
import useAutocomplete, { UseAutocompleteSingleProps } from '@material-ui/lab/useAutocomplete';
import { makeStyles, Theme } from '@material-ui/core/styles';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import Paper, { PaperProps } from '@material-ui/core/Paper';
import cx from 'clsx';

const useStyles = makeStyles<Theme>((theme: Theme) => {
  const marginOffset = theme.spacing(3);
  return {
    addIcon: {
      fontSize: `${theme.typography.pxToRem(12)} !important`
    },
    input: {
      width: 200
    },
    textField: {
      marginTop: marginOffset
    },
    listItem: {
      padding: `${theme.spacing(0.5)}px ${theme.spacing(1.5)}px`
    },
    formButtonContainer: {
      float: 'right',
      margin: `0 auto`,
      padding: '0 10%'
    },
    submitButton: {
      marginLeft: theme.spacing(1.5)
    },
    formDropdown: {
      width: '100%',
      position: 'relative',
      margin: 0,
      padding: 0,
      marginTop: theme.spacing(1),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      borderRadius: 0,
      zIndex: 1,
      overflow: 'auto'
    },
    dropdown: {
      width: '100%',
      position: 'relative',
      borderRadius: theme.shape.borderRadius,
      margin: 0,
      padding: 0,
      marginTop: theme.spacing(0.5),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      zIndex: 1,
      overflow: 'auto',
      border: '1px solid #000'
    },
    listbox: {
      listStyle: 'none',
      backgroundColor: theme.palette.background.paper,

      '& li[data-focus="true"]': {
        backgroundColor: `${theme.palette.grey[300]} !important`,
        cursor: 'pointer'
      },
      '& li:active': {
        backgroundColor: `${theme.palette.grey[300]} !important`,
        cursor: 'pointer'
      }
    },
    openFormSection: {
      padding: `${theme.spacing(0.5)}px ${theme.spacing(1.5)}px`,
      cursor: 'pointer'
    }
  };
});

function boldMatches(optionString: string, value: string): string | React.ReactElement {
  const index = optionString
    .toLowerCase()
    .search(value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').toLowerCase());

  if (index < 0) return optionString;
  return (
    <span>
      {optionString.slice(0, index)}
      <b>{optionString.slice(index, index + value.length)}</b>
      {optionString.slice(index + value.length, optionString.length)}
    </span>
  );
}

export interface SearchOrAddProps<T> extends UseAutocompleteSingleProps<T> {
  listFn: (option: T) => string;
  idFn: (option: T) => string;
  clearOnSelect?: boolean;
  onSelect: (event?: React.MouseEvent<HTMLLIElement>) => void;
  TextFieldProps?: TextFieldProps;
  formSubmit?: React.MouseEventHandler<HTMLButtonElement>;
  FormComponent?: React.ReactNode;
  label?: string;
  LabelProps?: TypographyProps & { component: React.ElementType };
  FormContainerProps?: PaperProps;
  openFormButtonLabel?: React.ReactNode;
  openFormButtonLabelAction?: React.ReactNode;
  OpenFormButtonProps?: ButtonProps;
  SubmitFormButtonProps?: ButtonProps;
  CancelFormButtonProps?: ButtonProps;
  submitButtonLabel?: string;
  cancelButtonLabel?: string;
  RootProps?: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;
  BoxListProps?: React.DetailedHTMLProps<React.HTMLAttributes<HTMLUListElement>, HTMLUListElement>;
  BoxListItemProps?: TypographyProps & { component: React.ElementType };
  margin?: boolean;
}

function SearchOrAdd<T>({
  TextFieldProps,
  formSubmit,
  onSelect,
  LabelProps,
  RootProps,
  BoxListProps,
  BoxListItemProps,

  SubmitFormButtonProps,
  CancelFormButtonProps,
  FormContainerProps,
  submitButtonLabel,
  clearOnSelect,
  cancelButtonLabel = 'Cancel',
  listFn,
  idFn,
  id,
  options,
  FormComponent,
  openFormButtonLabel,
  openFormButtonLabelAction,
  margin = true,
  label,
  ...rest
}: SearchOrAddProps<T>): React.ReactElement {
  const classes = useStyles();

  const { inputProps, onBlur, onFocus, onChange, ...restTextFieldProps } = TextFieldProps || {};

  const [formOpen, setFormOpen] = React.useState(false);
  const [fieldValue, setFieldValue] = React.useState('');
  const [focused, setFocused] = React.useState(false);

  const handleFocus: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (
    event
  ): void => {
    if (onFocus) {
      onFocus(event);
    }
    setFocused(true);
    setFieldValue(fieldValue.trim());
  };

  const handleBlur: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (
    event
  ): void => {
    if (onBlur) {
      onBlur(event);
    }
    setFocused(false);
  };

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event): void => {
    if (onChange) {
      onChange(event);
    }
    setFieldValue((event as React.ChangeEvent<HTMLInputElement>).target.value);
  };

  const handleForm = React.useCallback((): void => {
    setFormOpen(!formOpen);
  }, [setFormOpen, formOpen]);

  const handleSubmit = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      if (formSubmit) {
        formSubmit(event);
      }
      handleForm();
    },
    [formSubmit, handleForm]
  );
  const handleSelect = (event: React.MouseEvent<HTMLLIElement>): void => {
    if (onSelect) {
      onSelect(event);
    }
    if (clearOnSelect) {
      setFieldValue('');
    }
  };

  const {
    getRootProps,
    getInputProps,
    getInputLabelProps,
    getListboxProps,
    getOptionProps,
    groupedOptions
  } = useAutocomplete({
    getOptionLabel: listFn,
    id,
    blurOnSelect: true,
    selectOnFocus: true,
    openOnFocus: true,
    clearOnEscape: true,
    onOpen: () => setFieldValue(''),
    onClose: () => setFocused(false),
    options,
    ...rest
  });

  return (
    <>
      <div {...getRootProps()} {...RootProps}>
        <Typography
          component="label"
          variant="subtitle2"
          className={classes.label}
          {...getInputLabelProps()}
          {...LabelProps}
        >
          {label}
        </Typography>
        <TextField
          className={cx({ [classes.textField]: margin })}
          onChange={handleChange}
          value={fieldValue}
          onFocus={handleFocus}
          onBlur={handleBlur}
          inputProps={{ ...getInputProps(), ...inputProps, value: fieldValue }}
          {...restTextFieldProps}
        />
      </div>
      {/* TODO: remove Boolean(groupedOptions.length) condition when invites are possible */}
      {focused &&
        !formOpen &&
        Boolean(groupedOptions.length) &&
        (Boolean(options.length) || fieldValue) && (
          <ul
            className={cx(classes.listbox, classes.dropdown)}
            {...getListboxProps()}
            {...BoxListProps}
          >
            {groupedOptions.map((option, index) => {
              const { onClick, ...restOptionProps } = getOptionProps({ option, index }) as any;
              const handleOptionClick = (event: React.MouseEvent<HTMLLIElement>): void => {
                onClick(event);
                handleSelect(event);
              };
              return (
                <Typography
                  component="li"
                  variant="body1"
                  className={classes.listItem}
                  key={`item-${listFn(option)}`}
                  {...restOptionProps}
                  {...BoxListItemProps}
                  id={idFn(option)}
                  onClick={handleOptionClick}
                >
                  {boldMatches(listFn(option), fieldValue)}
                </Typography>
              );
            })}
            {/*FormComponent && fieldValue && (
            <div className={classes.openFormSection} onClick={handleForm}>
              <Typography variant="body1">{openFormButtonLabel}</Typography>
              <Typography variant="body1" color="secondary">
                {openFormButtonLabelAction}
              </Typography>
            </div>
          )*/}
          </ul>
        )}

      {formOpen && (
        <Paper className={classes.formDropdown} elevation={2} {...FormContainerProps}>
          {FormComponent}

          <div className={classes.formButtonContainer}>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              disableElevation
              onClick={handleForm}
              {...CancelFormButtonProps}
            >
              {cancelButtonLabel}
            </Button>
            <Button
              onClick={handleSubmit}
              className={classes.submitButton}
              variant="contained"
              size="small"
              color="secondary"
              disableElevation
              startIcon={<AddCircleIcon />}
              {...SubmitFormButtonProps}
            >
              {submitButtonLabel}
            </Button>
          </div>
        </Paper>
      )}
    </>
  );
}

export default SearchOrAdd;
